import Quill from "quill";
import { Delta } from "quill/core";
import "quill/dist/quill.snow.css";
import { useEffect, useLayoutEffect, useRef } from "react";
import style from "./wysiwyg.module.scss";

export type WysiwygResize = "none" | "vertical" | "horizontal" | "both";

type WysiwygProps = {
  readOnly?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  resize?: WysiwygResize;
};

/**
 * Code grandement inspiré de https://medium.com/@naufal.zufar.s/solving-react-quill-deprecated-error-by-migrating-to-quill-js-9b49ef745c20
 * Changement de lib suite à des problèmes de fonctionnement avec `react-quill-new`et sa gestion des listes à puces.
 */
export function Wysiwyg({
  readOnly = false,
  value,
  onChange,
  placeholder,
  resize = "none",
}: WysiwygProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const quillRef = useRef<Quill | null>(null);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const quill = new Quill(container, {
      theme: "snow",
      readOnly,
      placeholder,
      // Restreint aussi le HTML issu d'un copié/collé : sans cette liste, Quill
      // conserve liens, styles en ligne et images base64, que le WAF rejette (403).
      formats: ["bold", "italic", "underline", "list"],
      modules: {
        toolbar: [
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
        ],
      },
    });
    quillRef.current = quill;

    quill.on(Quill.events.TEXT_CHANGE, () => {
      onChangeRef.current?.(quill.getSemanticHTML());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    if (quill.getSemanticHTML() === value) return;
    if (value) {
      const isHTML = /^(<[a-z]+[^>]*>[\s\S]*?<\/[a-z]+>)+$/i.test(value);
      const contentValue = isHTML
        ? quill.clipboard.convert({ html: value })
        : new Delta().insert(value);
      quill.setContents(contentValue);
    } else {
      quill.setContents(new Delta());
    }
  }, [value]);

  return (
    <div
      className={style.wrapper}
      data-resize={resize === "none" ? undefined : resize}
      data-read-only={readOnly || undefined}
    >
      <div ref={containerRef} />
    </div>
  );
}
